import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderIcon from '@mui/icons-material/Folder';
import GroupIcon from '@mui/icons-material/Group';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useAtom, useAtomValue } from 'jotai';
import { type ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authUserAtom } from '@consentropy/shared';
import { sidebarCollapsedAtom } from '@consentropy/shared';
import { ROUTE_PATHS } from '../../routes/routePaths';
import consentropyLogo from '../../assets/Consentropy_new_logo.svg';
import { BuildInfo } from './BuildInfo';

export const SIDEBAR_WIDTH = 260;
export const SIDEBAR_COLLAPSED_WIDTH = 64;

interface NavItem {
  label: string;
  to: string;
  icon: ReactElement;
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useAtom(sidebarCollapsedAtom);
  const user = useAtomValue(authUserAtom);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  const navItems: NavItem[] = [
    { label: 'Dashboard', to: ROUTE_PATHS.DASHBOARD, icon: <DashboardIcon /> },
    { label: 'Projects', to: ROUTE_PATHS.PROJECTS, icon: <FolderIcon /> },
    { label: 'Profile', to: ROUTE_PATHS.PROFILE, icon: <PersonIcon /> },
    { label: 'Settings', to: ROUTE_PATHS.SETTINGS, icon: <SettingsIcon /> },
  ];

  if (user?.role === 'platform_admin') {
    navItems.splice(
      2,
      0,
      { label: 'Clients', to: ROUTE_PATHS.ADMIN_CLIENTS, icon: <GroupIcon /> },
      { label: 'Users', to: ROUTE_PATHS.ADMIN_USERS, icon: <ManageAccountsIcon /> },
    );
  }

  return (
    <Drawer
      variant="permanent"
      slotProps={{
        paper: {
          sx: {
            width,
            transition: 'width 0.2s ease',
            overflowX: 'hidden',
            border: 0,
            borderRight: isDark ? '1px solid rgba(0,181,226,0.3)' : 0,
            background: isDark
              ? 'linear-gradient(170deg, #0A1A3A 0%, #0F2550 55%, #0D3A5A 145%)'
              : 'linear-gradient(170deg, #002D72 0%, #003A88 55%, #00B5E2 145%)',
            color: '#FFFFFF',
            boxShadow: '2px 0 24px ' + alpha(theme.palette.primary.main, 0.08),
          },
        },
      }}
    >
      <Stack sx={{ height: '100%' }}>
        <Box sx={{ px: collapsed ? 1 : 3, pt: 3.25, pb: 2.5 }}>
          <Box
            sx={{
              width: '100%',
              minHeight: 46,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              opacity: collapsed ? 0 : 1,
              overflow: 'hidden',
              position: 'relative',
              transition: 'opacity 0.2s ease',
            }}
          >
            <Box
              component="img"
              src={consentropyLogo}
              alt="Consentropy"
              sx={{
                width: '100%',
                maxWidth: 206,
                height: 'auto',
                maxHeight: 46,
                objectFit: 'contain',
                objectPosition: 'left center',
                display: 'block',
                opacity: 0.98,
                filter: 'brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0, 0, 0, 0.22))',
              }}
            />
          </Box>
        </Box>

        <List sx={{ px: 1.25, pt: 1.25, flexGrow: 1 }}>
          {navItems.map((item) => {
            const selected =
              location.pathname === item.to ||
              location.pathname.startsWith(item.to + '/');

            return (
              <ListItemButton
                key={item.to}
                selected={selected}
                onClick={() => navigate(item.to)}
                sx={{
                  borderRadius: 2,
                  mb: 0.75,
                  color: '#FFFFFF',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(0,181,226,0.22)',
                    boxShadow: '0 0 10px rgba(0,181,226,0.18)',
                    borderLeft: '3px solid #00B5E2',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'rgba(0,181,226,0.30)',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0,181,226,0.10)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: '#FFFFFF', minWidth: 36 }}>{item.icon}</ListItemIcon>
                {!collapsed ? <ListItemText primary={item.label} /> : null}
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ px: 1.25, pb: 1.25 }}>
          <BuildInfo collapsed={collapsed} />
          <IconButton
            onClick={() => setCollapsed((prev) => !prev)}
            sx={{ color: '#FFFFFF', borderRadius: 2, width: '100%' }}
          >
            {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
          </IconButton>
        </Box>
      </Stack>
    </Drawer>
  );
}
